import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ManagementHttpClient } from './management-http-client';
import { QueryStringBuilder } from '@bayclubs/utils';
import { BookClassCommand, BookClassModel, CalendarModel, ConflictModel, CopyGroupClassCommandOptions, CopyGroupClassCommandResult, CopyGroupClassesScheduleCommand, CreateGroupClassCommand, CreateGroupClassesCommandResult, EditGroupClassCommand, GetChangedFieldsQuery, GetGroupClassesCalendarQuery, GetGroupClassesQuery, GroupClassChangedFieldsModel, GroupClassDetailsModel, GroupClassesDetailsModel, GroupClassesModel, GroupClassParticipantsModel, GroupClassWaitListModel, LocationResourcesModel, SetRealAttendanceApiCommand } from '@bayclubs/group-classes-employee-api';

@Injectable({
    providedIn: 'root'
})
export class GroupClassesApiService {
    constructor(private http: ManagementHttpClient) {}

    public get(criteria: GetGroupClassesQuery): Observable<GroupClassesModel> {
        return this.http.get<GroupClassesModel>(
            `groupclasses?` + QueryStringBuilder.buildParametersFromSearch(criteria).toString(),
            true
        );
    }

    public getCalendar(criteria: GetGroupClassesCalendarQuery): Observable<CalendarModel> {
        return this.http.get<CalendarModel>(
            `groupclasses/calendar?` + QueryStringBuilder.buildParametersFromSearch(criteria).toString(),
            true
        );
    }

    public getDetails(id: string, showLoader: boolean = true): Observable<GroupClassDetailsModel> {
        return this.http.get<GroupClassDetailsModel>(`groupclasses/` + id, showLoader);
    }

    public getManyDetails(ids: string[], showLoader: boolean = true): Observable<GroupClassesDetailsModel> {
        return this.http.post<GroupClassesDetailsModel>(`groupclasses/many/`, { ids: ids }, showLoader);
    }

    public participants(id: string): Observable<GroupClassParticipantsModel> {
        return this.http.get<GroupClassParticipantsModel>(`groupclasses/` + id + `/participants`, true);
    }

    public getWaitList(id: string): Observable<GroupClassWaitListModel> {
        return this.http.get<GroupClassWaitListModel>(`groupclasses/` + id + `/waitlist`, true);
    }

    public save(request: CreateGroupClassCommand): Observable<CreateGroupClassesCommandResult> {
        return this.http.post<CreateGroupClassesCommandResult>(`groupclasses`, request, true);
    }

    public edit(request: EditGroupClassCommand): Observable<any> {
        return this.http.put<any>(`groupclasses`, request, true);
    }

    public substitute(groupClassId: string, substituteInstructorId: string): Observable<any> {
        return this.http.put<any>(`groupclasses/${groupClassId}/substitute/${substituteInstructorId}`, null, true);
    }

    public cancelSubstitution(groupClassId: string): Observable<any> {
        return this.http.put<any>(`groupclasses/${groupClassId}/cancelsubstitution`, null, true);
    }

    public cancel(id: string, includeFollowingOccurrences: boolean): Observable<any> {
        return this.http.post<any>(
            `groupclasses/${id}/cancel?includeFollowingOccurrences=${includeFollowingOccurrences}`,
            null,
            true
        );
    }

    public restore(id: string): Observable<any> {
        return this.http.post<any>(`groupclasses/${id}/restore`, null, true);
    }

    public getNames(): Observable<Array<string>> {
        return this.http.get<Array<string>>(`groupclasses/names`, false);
    }

    public delete(id: string, includeFollowingOccurrences: boolean): Observable<any> {
        return this.http.delete<any>(`groupclasses/${id}?includeFollowingOccurrences=${includeFollowingOccurrences}`, true);
    }

    public copySingle(
        id: string,
        request: CopyGroupClassCommandOptions
    ): Observable<CopyGroupClassCommandResult> {
        return this.http.post<CopyGroupClassCommandResult>(`groupclasses/` + id + `/copy`, request, true);
    }

    public copySchedule(
        request: CopyGroupClassesScheduleCommand
    ): Observable<CopyGroupClassCommandResult> {
        return this.http.post<CopyGroupClassCommandResult>(`groupclasses/copyschedule`, request, true);
    }

    public conflict(groupClassId: string, instructorId: string): Observable<ConflictModel> {
        return this.http.get<ConflictModel>(`groupclasses/${groupClassId}/conflict/${instructorId}`, true);
    }

    public setRealAttendance(groupClassId: string, realAttendance: SetRealAttendanceApiCommand): Observable<any> {
        return this.http.put<any>(`groupclasses/${groupClassId}/attendance`, realAttendance);
    }

    public getChangedFields(
        request: GetChangedFieldsQuery
    ): Observable<GroupClassChangedFieldsModel> {
        return this.http.post<GroupClassChangedFieldsModel>(`groupclasses/changed-fields`, request, true);
    }

    public detectFutureOccurrences(groupClassId: string): Observable<boolean> {
        return this.http.get<boolean>(`groupclasses/${groupClassId}/future-occurrences`, true);
    }

    public cancelBooking(groupClassBookingId: string): Observable<any> {
        return this.http.post<any>('bookings/' + groupClassBookingId + '/cancel/', {}, true);
    }

    public bookForMember(bookClassCommand: BookClassCommand): Observable<BookClassModel> {
        return this.http.post<BookClassModel>('bookings', bookClassCommand, true);
    }

    public getLocationResources(groupClassId: string): Observable<LocationResourcesModel> {
        return this.http.get<LocationResourcesModel>(`groupclasses/${groupClassId}/location-resources`, true);
    }
}
